import React from 'react';
import type { ChecklistItem } from '../types/checklist';

interface InstructionsPanelProps {
  item: ChecklistItem;
  onClose: () => void;
}

export function InstructionsPanel({ item, onClose }: InstructionsPanelProps) {
  return (
    <div className="absolute inset-x-0 inset-y-0 sm:top-[100px] sm:bottom-[100px] md:top-[110px] md:bottom-[110px] bg-navy-800 border border-navy-700 z-10 rounded-lg mx-2 my-2 shadow-xl flex flex-col">
      {/* Header com título e botão fechar */}
      <div className="bg-navy-700 p-2 sm:p-3 flex items-center justify-between border-b border-navy-600">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-white text-xs sm:text-sm">Instruções</h3>
          <span className="text-xs text-gray-400 bg-navy-600 px-2 py-0.5 rounded">
            {item.automated ? 'Automático' : 'Manual'}
          </span>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 text-gray-400 hover:text-white hover:bg-navy-600 rounded-lg transition-colors"
          title="Fechar instruções"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Conteúdo principal */}
      <div className="flex-1 p-3 sm:p-4 text-xs sm:text-sm text-gray-300 overflow-auto">
        {/* Título do item */}
        <div className="mb-3 sm:mb-4">
          <h4 className="font-medium text-white text-sm sm:text-base mb-2">{item.text}</h4>
          <p className="text-gray-400 leading-relaxed">{item.description}</p>
        </div>

        {/* Área para imagens */}
        <div className="mb-3 sm:mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
            {/* Imagem de Referência */}
            <div className="bg-navy-700/50 rounded-lg p-3 border border-navy-600/50">
              <div className="text-xs text-gray-500 mb-2 font-medium">Imagem de Referência</div>
              {item.images?.reference ? (
                <div className="aspect-video bg-navy-600 rounded overflow-hidden">
                  <img 
                    src={item.images.reference} 
                    alt="Referência" 
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="aspect-video bg-navy-600 rounded flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <svg className="w-8 h-8 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <path d="M21 15l-5-5L5 21" />
                    </svg>
                    <span className="text-xs">Sem imagem</span>
                  </div>
                </div>
              )}
            </div>

            {/* Imagem de Exemplo */}
            <div className="bg-navy-700/50 rounded-lg p-3 border border-navy-600/50">
              <div className="text-xs text-gray-500 mb-2 font-medium">Imagem de Exemplo</div>
              {item.images?.example ? (
                <div className="aspect-video bg-navy-600 rounded overflow-hidden">
                  <img 
                    src={item.images.example} 
                    alt="Exemplo" 
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="aspect-video bg-navy-600 rounded flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <svg className="w-8 h-8 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <path d="M21 15l-5-5L5 21" />
                    </svg>
                    <span className="text-xs">Sem imagem</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Status do item */}
        <div className={`p-3 rounded-lg border ${
          item.automated 
            ? 'bg-green-500/10 border-green-500/20 text-green-300' 
            : 'bg-blue-500/10 border-blue-500/20 text-blue-300'
        }`}>
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {item.automated ? (
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              ) : (
                <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              )}
            </svg>
            <span className="text-sm font-medium">
              {item.automated 
                ? 'Verificação automática pelo sistema' 
                : 'Verificação manual necessária'
              }
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}